import { AxiosError } from 'axios'
import {
  DecodedVehicleResponse,
  ISaveVehicleToHistory,
  VehicleDetail,
} from '../api/vehicle/interfaces'
import { BaseServiceProvider } from './BaseServiceProvider'
import {
  VehicleDetail as VehicleHistoryDetail,
  VehicleHistoryResp,
  VehiclePreviousSearches,
  VehicleResponseError,
  VehicleSearchCriteria,
} from '../store/vehicleHistory/interfaces'
import { IVehicleInfo } from '../store/vehicleHistory/VehicleHistoryStore'
import { StoreInstances } from '../store/StoreInstancesContainer'
import { ISearchHistorySearchCriteria } from '../features/vehicleHistory/components/SubVehicleRow'
import { IPartsBasketVehicleInfo } from '../api/embedded/types/partsBasket-1.1'
import { Vehicle, decodeSource } from '../store/models/Vehicles'
import { TableColumn } from '../features/search/VehicleSearch/interfaces'
import CatalogServiceProvider from './CatalogServiceProvider'
import { VehicleSelectorStep } from '../ui-components/util/Interfaces'
import { StorageKeys, Translate2 } from 'common'
import {
  getSavedVehicleRegion,
  getVehicleRegionId,
} from 'libs/cart/src/helpers/vehicleDataStorage'

class VehicleServiceProvider extends BaseServiceProvider {
  constructor() {
    super(import.meta.env.VITE_API_BASE_URL, 'vehicle', '1.2', '')
  }

  getVehicleTypes = (): Array<TableColumn> => {
    const catalogs = StoreInstances.customizeSearchStore.getVehicleTypes()
    const translatedCatalogs = catalogs.map((item) => ({
      ...item,
      value: Translate2(item.value),
    }))

    const cols: Array<TableColumn> = CatalogServiceProvider.initTableCols(1)
    cols[0].options = translatedCatalogs
    return cols
  }

  /**
   * Wrapper to simplify API calls for all vehicle option steps
   * @param step
   * @param currentSelections
   */
  getSelectorOptions = async (
    step: VehicleSelectorStep,
    currentSelections?: Vehicle,
    filterMakes?: boolean
  ): Promise<Array<TableColumn>> => {
    switch (step) {
      case VehicleSelectorStep.TYPE:
        return this.getVehicleTypes()
      case VehicleSelectorStep.YEAR:
        return CatalogServiceProvider.getYearOptions()
      case VehicleSelectorStep.MAKE:
        return CatalogServiceProvider.getMakeOptions(
          currentSelections,
          filterMakes
        )
      case VehicleSelectorStep.MODEL:
        return CatalogServiceProvider.getModelOptions(currentSelections)
      case VehicleSelectorStep.SUBMODEL:
        return CatalogServiceProvider.getSubModelOptions(currentSelections)
      case VehicleSelectorStep.ENGINE:
        return currentSelections?.model
          ? CatalogServiceProvider.getEngineOptions(currentSelections)
          : []
      default:
        return []
    }
  }

  getVehicleHistory = async (
    start?: number,
    limit?: number,
    filters?: VehicleSearchCriteria
  ): Promise<VehicleHistoryResp> => {
    const vehicleSearchCriteria: VehicleSearchCriteria = {
      isFavorite: filters?.isFavorite || false,
      name: filters?.name || '',
      groupName: '',
      lookupSource:
        (filters?.lookupSource === 'All' ? '' : filters?.lookupSource) || '',
      fromDate: filters?.fromDate || null,
      toDate: filters?.toDate || new Date().toISOString(),
    }
    let resp
    const urlString = `vehicle/history?filter=${JSON.stringify(
      vehicleSearchCriteria
    )}&isSpecificVehicle=false&start=${start}&limit=${limit}`
    const urlEncoded = encodeURI(urlString)
    try {
      resp = await this.get<VehicleHistoryResp>(urlEncoded)
      if (resp.status === 200) {
        return resp.data
      }
      if (resp.status === 204) {
        return {
          totalVehicles: 0,
          vehicles: [],
        }
      }
    } catch (error) {
      throw new Error(error.toString())
    }
    return {}
  }

  getVehicleById = async (vehicleId: number): Promise<VehicleDetail> => {
    let resp
    try {
      resp = await this.get<VehicleDetail>(`vehicle/byId/${vehicleId}`)
      if (resp.status === 200) {
        return resp.data
      }
    } catch (error) {
      throw new Error(error.toString())
    }
    return null
  }

  updateVehicle = async (
    vehicleId: number,
    vehicle: IVehicleInfo
  ): Promise<void> => {
    try {
      await this.patch<void>(`vehicle/byId/${vehicleId}`, vehicle)
    } catch (error) {
      const { message }: VehicleResponseError = (error as AxiosError).response
        .data as VehicleResponseError
      if (message === null || message === '' || message === undefined) {
        StoreInstances.uiStore.displayErrorNotification(
          'errorOccurredWhileUpdatingVehicle'
        )
      } else {
        StoreInstances.uiStore.displayErrorNotification(message)
      }
      throw new Error(error.toString())
    }
  }

  getPreviousVehicleSearches = async (
    vehicleId: number,
    searchCriteria: ISearchHistorySearchCriteria
  ): Promise<Array<VehiclePreviousSearches>> => {
    try {
      const response = await this.get<VehiclePreviousSearches[]>(
        `vehicle/${vehicleId}/searchHistory`,
        {
          params: {
            filter: JSON.stringify(searchCriteria),
          },
        }
      )
      return response.data
    } catch (error) {
      throw new Error(error.toString())
    }
  }

  addVehicleToHistory = async (vehicle: Vehicle): Promise<Vehicle> => {
    // eslint-disable-next-line no-useless-catch
    try {
      const region = StoreInstances.userStore.isCclEnabled
        ? (vehicle.regionId ??
          getVehicleRegionId(
            vehicle,
            StoreInstances?.searchStore?.cclVehiclesData,
            StoreInstances?.userStore?.regionId
          ))
        : undefined
      const saveVehicleToHistory: ISaveVehicleToHistory = {
        id: Number(vehicle.id ?? 0),
        stdId: 0,

        name: vehicle.name ?? '',

        year: vehicle.year?.value ?? '',
        yearCode: vehicle.year?.id ?? 0,

        make: vehicle.make?.value ?? '',
        makeCode: vehicle.make?.id ?? 0,

        modelType: vehicle.modelType?.value ?? '',
        modelTypeCode: vehicle.modelType?.id ?? 0,

        model: vehicle.model?.value ?? '',
        modelCode: vehicle.model?.id ?? 0,

        ...(vehicle.subModel?.id && vehicle.subModel?.value
          ? {
              subModel: vehicle.subModel.value,
              subModelCode: vehicle.subModel.id,
            }
          : {}),

        engine: vehicle.engine?.value ?? '',
        engineCode: vehicle.engine?.id ?? 0,

        vin: vehicle.vin,
        state: vehicle.state,
        plate: vehicle.plate,

        decodeSource: vehicle.decodeSource ?? decodeSource.Ymme,
        type: vehicle.type,
        regionId: region,
      }
      localStorage.setItem(
        StorageKeys.SAVED_VEHICLE_REGION_ID,
        JSON.stringify(region)
      )

      const response = await this.post<ISaveVehicleToHistory>(
        'vehicle',
        saveVehicleToHistory,
        {
          params: { groupName: 'TEST' },
        }
      )

      const data = response.data
      /**   API response to Vehicle interface */
      const mappedVehicle: Vehicle = {
        initialized: true,
        id: data.id,
        name: data.name,

        type: data.type,

        year: {
          id: data.yearCode,
          value: data.year,
        },

        make: {
          id: data.makeCode,
          value: data.make,
        },

        modelType: {
          id: data.modelTypeCode,
          value: data.modelType,
        },

        model: {
          id: data.modelCode,
          value: data.model,
        },
        ...(data.subModelCode && data.subModel
          ? {
              subModel: {
                id: data.subModelCode,
                value: data.subModel,
              },
            }
          : {}),

        engine: {
          id: data.engineCode,
          value: data.engine,
        },

        vin: data.vin ?? undefined,
        state: data.state ?? undefined,
        plate: data.plate ?? undefined,

        decodeSource: data.decodeSource,

        // save regionId into mapped vehicle data.
        regionId: StoreInstances.userStore.isCclEnabled
          ? (data.regionId ??
            getVehicleRegionId(
              vehicle,
              StoreInstances?.searchStore?.cclVehiclesData,
              StoreInstances?.userStore?.regionId
            ))
          : undefined,
      }

      return mappedVehicle
    } catch (error) {
      throw error
    }
  }

  deleteVehicleHistory = async (id) => {
    await this.post('vehicle/delete', {
      ids: [id],
    })
  }

  /**
   * Returns a decoded and validated Vehicle based on the information provided from the integration partner
   */
  getDecodedAndValidatedYMME = async (
    partsBasketVehicle: IPartsBasketVehicleInfo,
    catId?: number,
    regionIds?: number[]
  ): Promise<Vehicle> => {
    await CatalogServiceProvider.getActiveCatalogPublisher()
    StoreInstances.searchStore.setCclVehicleData(regionIds)
    const decodedVehicleResp = await this.post<DecodedVehicleResponse>(
      'vehicle/decode',
      {
        vehicleInfo: partsBasketVehicle,
      },
      {
        params: {
          catalogId: catId ?? 111,
          covListId: StoreInstances.userStore.preferences?.mclOrgId,
          countryId: StoreInstances.userStore.country?.countryCode,
          mclOrgId: StoreInstances.userStore.preferences?.mclOrgId,
          regionId: StoreInstances.userStore.isCclEnabled
            ? regionIds?.length === 1
              ? regionIds[0]
              : regionIds?.includes(StoreInstances.userStore.regionId)
                ? StoreInstances.userStore.regionId
                : regionIds?.includes(19)
                  ? 19
                  : (regionIds?.[0] ?? StoreInstances.userStore.regionId)
            : undefined,
        },
      }
    )

    const decodedVehicle = decodedVehicleResp.data
    StoreInstances.userStore.decodeRegionId = decodedVehicle.regionId
    return {
      id: decodedVehicle.id,
      name: decodedVehicle.name,
      vin: decodedVehicle.vin,
      state: decodedVehicle.state,
      plate: decodedVehicle.plate,
      decodeSource: decodedVehicle.decodeSource,
      type: decodedVehicle.type,
      year: {
        id: decodedVehicle.yearCode,
        value: decodedVehicle.year,
      },
      make: {
        id: decodedVehicle.makeCode,
        value: decodedVehicle.make,
      },
      modelType: {
        id: decodedVehicle.modelTypeCode,
        value: decodedVehicle.modelType,
      },
      model: {
        id: decodedVehicle.modelCode,
        value: decodedVehicle.model,
      },
      ...(decodedVehicle?.subModelCode && decodedVehicle?.subModel
        ? {
            subModel: {
              id: decodedVehicle.subModelCode,
              value: decodedVehicle.subModel,
            },
          }
        : {}),

      engine: {
        id: decodedVehicle.engineCode,
        value: decodedVehicle.engine,
      },
    }
  }

  getVehicleDetails = async (vin: string): Promise<VehicleHistoryDetail> => {
    try {
      const response = await this.get<VehicleHistoryDetail>(
        `vehicle/vin/${vin}/`,
        {
          params: {
            catalogId: 111,
            covListId: StoreInstances.userStore.preferences?.mclOrgId,
            countryId: StoreInstances.userStore.country?.countryCode,
            mclOrgId: StoreInstances.userStore.preferences?.mclOrgId,
            regionId: StoreInstances.userStore.isCclEnabled
              ? (getSavedVehicleRegion() ?? StoreInstances.userStore.regionId)
              : undefined,
          },
        }
      )
      return response.data
    } catch (error) {
      throw new Error(JSON.stringify(error))
    }
  }

  getSelectedMakes = async (): Promise<string[]> => {
    return await CatalogServiceProvider.getSelectedMakes()
  }

  getAllMakeOptions = async (): Promise<Array<TableColumn>> => {
    return await CatalogServiceProvider.getAllMakeOptions()
  }

  updateCustomMakes = async (makes: Array<string>): Promise<void> => {
    await CatalogServiceProvider.updateCustomMakes(makes?.join(','))
  }

  updateMakesFilterStatus = async (status: boolean): Promise<void> => {
    return await CatalogServiceProvider.updateMakesFilterStatus(status)
  }

  isMakesFilterEnabled = async (): Promise<boolean> => {
    return await CatalogServiceProvider.isMakesFilterEnabled()
  }
}

export default new VehicleServiceProvider()
